// WorkflowDesignModal.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { generateKey } from 'utilities/Keys';
import { MISSION_TASK_HANDLED_BY, normalizeMissionTask } from 'missions/taskmodal/missionTaskModalUtils';
import {
  WORKFLOW_OUTCOME_COMPLETE,
  applyWorkflowCompleteSpineFromTaskOrder,
  applyWorkflowOutgoingRoutes,
  assignWorkflowSpineCanvas,
  createWorkflowOutcomeDraft,
  ensureWorkflowTemplateGraph,
  getWorkflowOutgoingEdges,
  listWorkflowTaskOutcomeKeys,
  normalizeWorkflowOutcomes,
  removeWorkflowTaskFromGraph,
  validateWorkflowGraph,
} from 'workflows/workflowGraphUtils';

// CSS
import './WorkflowDesignModal.css';

// Components
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import TextInput from 'components/textinput/TextInput';
import MissionTaskModal from 'missions/taskmodal/MissionTaskModal';
import WorkflowAddOutcomeModal from './WorkflowAddOutcomeModal';
import WorkflowDesignerCanvas from './WorkflowDesignerCanvas';
import WorkflowRenameOutcomeModal from './WorkflowRenameOutcomeModal';

const DEFAULT_WORKFLOW_ICON_KEY = 'account_tree';

function normalizeWorkflowIconKey(value) {
  return String(value || '').trim() || DEFAULT_WORKFLOW_ICON_KEY;
}

function listWorkflowStartTaskKeys(tasks, edges) {
  const keys = (Array.isArray(tasks) ? tasks : [])
    .map((task) => String(task?.key || '').trim())
    .filter(Boolean);
  const incoming = new Map(keys.map((key) => [key, 0]));
  (Array.isArray(edges) ? edges : []).forEach((edge) => {
    const toTaskKey = edge?.toTaskKey == null ? null : String(edge.toTaskKey || '').trim();
    if (!toTaskKey || !incoming.has(toTaskKey)) return;
    incoming.set(toTaskKey, (incoming.get(toTaskKey) || 0) + 1);
  });
  return keys.filter((key) => (incoming.get(key) || 0) === 0);
}

function isAbandonedNewStepTask(task) {
  const title = String(task?.title || '').trim();
  // Accept legacy "New step" drafts from before task-first copy.
  if (title !== 'New task' && title !== 'New step') return false;
  const assignees = Array.isArray(task?.assignees) ? task.assignees : [];
  const outcomes = Array.isArray(task?.outcomes) ? task.outcomes : [];
  return assignees.length === 0 && outcomes.length === 0;
}

/**
 * Drops leftover "New task" starts created when + opened the editor but the user cancelled.
 *
 * @param {object[]} tasks
 * @param {object[]} edges
 * @returns {{ tasks: object[], edges: object[], prunedKeys: string[] }}
 */
function pruneAbandonedNewStepStarts(tasks, edges) {
  const taskList = Array.isArray(tasks) ? tasks : [];
  const edgeList = Array.isArray(edges) ? edges : [];
  const startKeys = listWorkflowStartTaskKeys(taskList, edgeList);
  if (startKeys.length <= 1) {
    return { tasks: taskList, edges: edgeList, prunedKeys: [] };
  }

  const prunedKeys = startKeys.filter((key) => {
    const task = taskList.find((entry) => entry.key === key);
    return isAbandonedNewStepTask(task);
  });
  if (prunedKeys.length === 0) {
    return { tasks: taskList, edges: edgeList, prunedKeys: [] };
  }

  const prunedSet = new Set(prunedKeys);
  return {
    tasks: taskList.filter((task) => !prunedSet.has(task.key)),
    edges: edgeList.filter((edge) => (
      !prunedSet.has(edge.fromTaskKey) && !prunedSet.has(edge.toTaskKey)
    )),
    prunedKeys,
  };
}

/**
 * WorkflowDesignModal
 *
 * Full-screen workflow graph designer (Forms-like design surface).
 * Canvas edits stay local until Save. Saving or deleting a task in the
 * Edit Workflow Task modal also persists the whole workflow.
 *
 * @param {boolean} [isOpen]
 * @param {boolean} [isSaving]
 * @param {function} onClose
 * @param {function} [onDelete]
 * @param {function} onSave
 * @param {object|null} [template]
 * @param {array} [availableFields] - Host record schema fields for Bot task editors.
 * @param {array} [recordTypeOptions] - Record types for Create Record Bot jobs.
 * @param {string} [typeLabel] - Host record type label for Agent copy.
 * @param {string|null} [baseKey] - Workspace scope for member pickers.
 * @returns {ReactNode}
 */
const WorkflowDesignModal = React.memo(({
  availableFields = [],
  baseKey = null,
  isOpen = false,
  isSaving = false,
  onClose,
  onDelete,
  onSave,
  recordTypeOptions = [],
  template = null,
  typeLabel = '',
}) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [draftTitle, setDraftTitle] = useState('');
  const [draftIconKey, setDraftIconKey] = useState(DEFAULT_WORKFLOW_ICON_KEY);
  const [draftTasks, setDraftTasks] = useState([]);
  const [draftEdges, setDraftEdges] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [taskEditorOpen, setTaskEditorOpen] = useState(false);
  const [addOutcomeTask, setAddOutcomeTask] = useState(null);
  const [connectToTaskKey, setConnectToTaskKey] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [renameOutcomeOpen, setRenameOutcomeOpen] = useState(false);
  const [deleteOutcomeConfirmOpen, setDeleteOutcomeConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fitNonce, setFitNonce] = useState(0);
  const pendingCreationRef = useRef(null);
  const draftTitleRef = useRef('');
  const draftIconKeyRef = useRef(DEFAULT_WORKFLOW_ICON_KEY);
  const templateKey = String(template?.key || '').trim();

  useEffect(() => {
    draftTitleRef.current = draftTitle;
  }, [draftTitle]);

  useEffect(() => {
    draftIconKeyRef.current = draftIconKey;
  }, [draftIconKey]);

  useEffect(() => {
    if (!isOpen || !template) return;
    const graph = ensureWorkflowTemplateGraph(template);
    let tasks = assignWorkflowSpineCanvas(graph.tasks);
    let edges = graph.edges;

    // Migrate older top→bottom spines into a left→right row for the horizontal designer.
    const xs = tasks
      .map((task) => Number(task?.canvas?.x))
      .filter((value) => Number.isFinite(value));
    if (xs.length >= 2 && (Math.max(...xs) - Math.min(...xs)) < 48) {
      tasks = assignWorkflowSpineCanvas(tasks, { force: true, gapX: 280, startX: 80, y: 140 });
    }

    const pruned = pruneAbandonedNewStepStarts(tasks, edges);
    tasks = pruned.tasks;
    edges = pruned.edges;

    // Recover graphs broken by a deleted Complete connector (multiple starts).
    let didRepairCompleteSpine = false;
    const preRepairValidation = validateWorkflowGraph(tasks, edges);
    if (!preRepairValidation.ok && listWorkflowStartTaskKeys(tasks, edges).length > 1) {
      const repairedEdges = applyWorkflowCompleteSpineFromTaskOrder(edges, tasks);
      if (validateWorkflowGraph(tasks, repairedEdges).ok) {
        edges = repairedEdges;
        didRepairCompleteSpine = true;
      }
    }

    const nextTitle = String(template?.title || '').trim();
    const nextIconKey = normalizeWorkflowIconKey(template?.iconKey);
    draftTitleRef.current = nextTitle;
    draftIconKeyRef.current = nextIconKey;
    setDraftTitle(nextTitle);
    setDraftIconKey(nextIconKey);
    setDraftTasks(tasks);
    setDraftEdges(edges);
    setSaveError('');
    setEditingTask(null);
    setTaskEditorOpen(false);
    setAddOutcomeTask(null);
    setConnectToTaskKey('');
    setSelectedOutcome(null);
    setRenameOutcomeOpen(false);
    setDeleteOutcomeConfirmOpen(false);
    setDeleteConfirmOpen(false);
    setFitNonce(0);
    pendingCreationRef.current = null;

    if (pruned.prunedKeys.length > 0 || didRepairCompleteSpine) {
      void onSave?.({
        ...template,
        edges,
        tasks,
      }, {
        close: false,
        silent: true,
      });
    }
  // Hydrate only on open / template switch — not when parent refreshes after Save.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, templateKey]);

  const validation = useMemo(
    () => validateWorkflowGraph(draftTasks, draftEdges),
    [draftEdges, draftTasks],
  );

  const persistDesign = useCallback(async (tasks, edges, options = {}) => {
    const close = options.close === true;
    const silent = options.silent !== false;
    const nextTitle = String(
      options.title != null ? options.title : draftTitleRef.current
    ).trim();
    const nextIconKey = normalizeWorkflowIconKey(
      options.iconKey != null ? options.iconKey : draftIconKeyRef.current
    );

    if (close) {
      const nextValidation = validateWorkflowGraph(tasks, edges);
      if (!nextValidation.ok) {
        setSaveError(nextValidation.errors[0] || 'Fix playbook graph issues before saving.');
        return false;
      }
      if (!nextTitle) {
        setSaveError('Playbook title is required.');
        return false;
      }
    }

    setSaveError('');
    const didSave = await onSave?.({
      ...template,
      edges,
      iconKey: nextIconKey,
      tasks,
      title: nextTitle || String(template?.title || '').trim(),
    }, {
      close,
      silent,
    });

    if (didSave === false) {
      if (!silent) {
        setSaveError('Could not save playbook design.');
      }
      return false;
    }

    if (close) {
      onClose?.();
    }
    return true;
  }, [onClose, onSave, template]);

  const handleTitleBlur = useCallback(() => {
    const nextTitle = String(draftTitle || '').trim();
    const previousTitle = String(template?.title || '').trim();
    if (!nextTitle) {
      setDraftTitle(previousTitle);
      draftTitleRef.current = previousTitle;
    }
  }, [draftTitle, template?.title]);

  const handleIconSelect = useCallback((nextIconKey) => {
    const normalized = normalizeWorkflowIconKey(nextIconKey);
    if (normalized === draftIconKeyRef.current) return;
    draftIconKeyRef.current = normalized;
    setDraftIconKey(normalized);
  }, []);

  const handleGraphChange = useCallback(({ tasks, edges }) => {
    setDraftTasks(Array.isArray(tasks) ? tasks : []);
    setDraftEdges(Array.isArray(edges) ? edges : []);
  }, []);

  const handleAddTask = useCallback(() => {
    const key = `task_${generateKey()}`;
    const previousTail = draftTasks[draftTasks.length - 1] || null;
    const nextTask = normalizeMissionTask({
      key,
      title: 'New task',
      handledBy: MISSION_TASK_HANDLED_BY.PEOPLE,
      outcomes: [],
      canvas: {
        x: (Number(previousTail?.canvas?.x) || 80) + 280,
        y: Number(previousTail?.canvas?.y) || 140,
      },
    });

    const nextTasks = [...draftTasks, nextTask];
    let nextEdges = [...draftEdges];

    if (previousTail?.key) {
      // Replace terminal Complete on previous tail with link to the new task when present.
      nextEdges = nextEdges.filter((edge) => !(
        edge.fromTaskKey === previousTail.key
        && edge.outcomeKey === WORKFLOW_OUTCOME_COMPLETE
        && edge.toTaskKey == null
      ));
      const hasCompleteRoute = nextEdges.some((edge) => (
        edge.fromTaskKey === previousTail.key
        && edge.outcomeKey === WORKFLOW_OUTCOME_COMPLETE
      ));
      if (!hasCompleteRoute) {
        nextEdges.push({
          key: `edge_${previousTail.key}_complete_${key}`,
          fromTaskKey: previousTail.key,
          outcomeKey: WORKFLOW_OUTCOME_COMPLETE,
          toTaskKey: key,
        });
      }
    }

    nextEdges.push({
      key: `edge_${key}_complete_end`,
      fromTaskKey: key,
      outcomeKey: WORKFLOW_OUTCOME_COMPLETE,
      toTaskKey: null,
    });

    // Hold an unconfirmed draft until the task modal is saved — Cancel rolls it back.
    pendingCreationRef.current = {
      taskKey: key,
      tasks: draftTasks,
      edges: draftEdges,
    };
    setDraftTasks(nextTasks);
    setDraftEdges(nextEdges);
    setFitNonce((value) => value + 1);
    setEditingTask(nextTask);
    setTaskEditorOpen(true);
  }, [draftEdges, draftTasks]);

  const handleEditTask = useCallback((task) => {
    pendingCreationRef.current = null;
    setEditingTask(task || null);
    setTaskEditorOpen(Boolean(task));
  }, []);

  const handleTaskEditorOpenChange = useCallback((nextOpen) => {
    if (nextOpen) {
      setTaskEditorOpen(true);
      return;
    }

    const pending = pendingCreationRef.current;
    pendingCreationRef.current = null;
    setTaskEditorOpen(false);
    setEditingTask(null);

    // Cancel / dismiss while creating: roll back the unconfirmed task + edge wiring.
    if (pending?.taskKey) {
      setDraftTasks(Array.isArray(pending.tasks) ? pending.tasks : []);
      setDraftEdges(Array.isArray(pending.edges) ? pending.edges : []);
      setFitNonce((value) => value + 1);
    }
  }, []);

  /**
   * Routes an outcome from one existing task to another (no new task created).
   */
  const routeOutcomeToExistingTask = useCallback((fromTask, buttonLabel, toTaskKey) => {
    const fromTaskKey = String(fromTask?.key || '').trim();
    const cleanedLabel = String(buttonLabel || '').trim();
    const normalizedToTaskKey = String(toTaskKey || '').trim();
    if (!fromTaskKey || !cleanedLabel || !normalizedToTaskKey) return;
    if (fromTaskKey === normalizedToTaskKey) return;
    if (!draftTasks.some((task) => task.key === normalizedToTaskKey)) return;

    const existingTask = draftTasks.find((task) => task.key === fromTaskKey) || fromTask;
    if (String(existingTask?.handledBy || '').trim().toLowerCase() === MISSION_TASK_HANDLED_BY.BOT
      && cleanedLabel.toLowerCase() !== 'complete') {
      return;
    }

    const existingOutcomes = normalizeWorkflowOutcomes(existingTask?.outcomes, {
      handledBy: existingTask?.handledBy,
    });
    const isCompleteLabel = cleanedLabel.toLowerCase() === 'complete';
    const draftOutcome = isCompleteLabel
      ? { key: WORKFLOW_OUTCOME_COMPLETE, label: 'Complete' }
      : createWorkflowOutcomeDraft(
        cleanedLabel,
        existingOutcomes.map((entry) => entry.key),
      );

    const nextFromTask = normalizeMissionTask({
      ...existingTask,
      key: fromTaskKey,
      outcomes: isCompleteLabel
        ? existingOutcomes
        : [...existingOutcomes, draftOutcome],
    });

    const nextTasks = draftTasks.map((task) => (
      task.key === fromTaskKey ? { ...task, ...nextFromTask } : task
    ));

    const existingRoutes = getWorkflowOutgoingEdges(draftEdges, fromTaskKey).map((edge) => ({
      outcomeKey: edge.outcomeKey,
      toTaskKey: edge.toTaskKey,
    }));
    const withoutSameOutcome = existingRoutes.filter(
      (route) => route.outcomeKey !== draftOutcome.key,
    );
    const required = listWorkflowTaskOutcomeKeys(nextFromTask);
    const routes = required.map((outcomeKey) => {
      if (outcomeKey === draftOutcome.key) {
        return { outcomeKey, toTaskKey: normalizedToTaskKey };
      }
      const existing = withoutSameOutcome.find((route) => route.outcomeKey === outcomeKey);
      return {
        outcomeKey,
        toTaskKey: existing ? existing.toTaskKey : null,
      };
    });

    setDraftTasks(nextTasks);
    setDraftEdges(applyWorkflowOutgoingRoutes(draftEdges, fromTaskKey, routes));
    setConnectToTaskKey('');
    setAddOutcomeTask(null);
    setFitNonce((value) => value + 1);
  }, [draftEdges, draftTasks]);

  /**
   * Continues from a task: creates the next task, wires the outcome edge, opens the editor.
   */
  const continueFromTask = useCallback((fromTask, buttonLabel) => {
    const fromTaskKey = String(fromTask?.key || '').trim();
    const cleanedLabel = String(buttonLabel || '').trim();
    if (!fromTaskKey || !cleanedLabel) return;

    const fromCanvas = fromTask?.canvas && typeof fromTask.canvas === 'object'
      ? fromTask.canvas
      : (draftTasks.find((task) => task.key === fromTaskKey)?.canvas || null);

    const toTaskKey = `task_${generateKey()}`;
    const createdTask = normalizeMissionTask({
      key: toTaskKey,
      title: 'New task',
      handledBy: MISSION_TASK_HANDLED_BY.PEOPLE,
      outcomes: [],
      canvas: {
        x: (Number(fromCanvas?.x) || 80) + 280,
        y: Number(fromCanvas?.y) || 140,
      },
    });

    const existingTask = draftTasks.find((task) => task.key === fromTaskKey) || fromTask;
    const fromHandledBy = String(existingTask?.handledBy || '').trim().toLowerCase()
      === MISSION_TASK_HANDLED_BY.BOT
      ? MISSION_TASK_HANDLED_BY.BOT
      : MISSION_TASK_HANDLED_BY.PEOPLE;

    // Bots only continue on Complete — no custom outcomes.
    if (fromHandledBy === MISSION_TASK_HANDLED_BY.BOT
      && cleanedLabel.toLowerCase() !== 'complete') {
      return;
    }

    const existingOutcomes = normalizeWorkflowOutcomes(existingTask?.outcomes, {
      handledBy: fromHandledBy,
    });

    // Built-in Complete: route it instead of minting a duplicate custom outcome.
    const isCompleteLabel = cleanedLabel.toLowerCase() === 'complete';
    const draftOutcome = isCompleteLabel
      ? { key: WORKFLOW_OUTCOME_COMPLETE, label: 'Complete' }
      : createWorkflowOutcomeDraft(
        cleanedLabel,
        existingOutcomes.map((entry) => entry.key),
      );

    const nextFromTask = normalizeMissionTask({
      ...existingTask,
      key: fromTaskKey,
      handledBy: fromHandledBy,
      outcomes: isCompleteLabel
        ? existingOutcomes
        : [...existingOutcomes, draftOutcome],
    });

    const nextTasks = [
      ...draftTasks.map((task) => (
        task.key === fromTaskKey ? { ...task, ...nextFromTask } : task
      )),
      createdTask,
    ];

    const existingRoutes = getWorkflowOutgoingEdges(draftEdges, fromTaskKey).map((edge) => ({
      outcomeKey: edge.outcomeKey,
      toTaskKey: edge.toTaskKey,
    }));
    const withoutSameOutcome = existingRoutes.filter(
      (route) => route.outcomeKey !== draftOutcome.key,
    );
    const required = listWorkflowTaskOutcomeKeys(nextFromTask);
    const routes = required.map((outcomeKey) => {
      if (outcomeKey === draftOutcome.key) {
        return { outcomeKey, toTaskKey };
      }
      const existing = withoutSameOutcome.find((route) => route.outcomeKey === outcomeKey);
      return {
        outcomeKey,
        toTaskKey: existing ? existing.toTaskKey : null,
      };
    });

    const nextEdges = [
      ...applyWorkflowOutgoingRoutes(draftEdges, fromTaskKey, routes),
      {
        key: `edge_${toTaskKey}_complete_end`,
        fromTaskKey: toTaskKey,
        outcomeKey: WORKFLOW_OUTCOME_COMPLETE,
        toTaskKey: null,
      },
    ];

    // Hold an unconfirmed draft until the task modal is saved — Cancel rolls it back.
    pendingCreationRef.current = {
      taskKey: toTaskKey,
      tasks: draftTasks,
      edges: draftEdges,
    };
    setDraftTasks(nextTasks);
    setDraftEdges(nextEdges);
    setFitNonce((value) => value + 1);
    setAddOutcomeTask(null);
    setEditingTask(createdTask);
    setTaskEditorOpen(true);
  }, [draftEdges, draftTasks]);

  const handleOpenAddOutcome = useCallback((task) => {
    if (!task?.key) return;

    // Happy path: Complete isn't routed yet → skip the label ask.
    const completeIsRouted = getWorkflowOutgoingEdges(draftEdges, task.key).some((edge) => (
      edge.outcomeKey === WORKFLOW_OUTCOME_COMPLETE && edge.toTaskKey != null
    ));
    if (!completeIsRouted) {
      continueFromTask(task, 'Complete');
      return;
    }

    // Bots only have Complete — already routed, nothing more to add.
    if (String(task?.handledBy || '').trim().toLowerCase() === MISSION_TASK_HANDLED_BY.BOT) {
      return;
    }

    // Alternate path: ask for a custom route name (Bad Fit, etc.).
    setConnectToTaskKey('');
    setAddOutcomeTask(task);
  }, [continueFromTask, draftEdges]);

  const handleConnectTasks = useCallback(({ fromTaskKey, toTaskKey } = {}) => {
    const sourceKey = String(fromTaskKey || '').trim();
    const targetKey = String(toTaskKey || '').trim();
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;

    const fromTask = draftTasks.find((task) => task.key === sourceKey) || null;
    if (!fromTask) return;
    if (!draftTasks.some((task) => task.key === targetKey)) return;

    const completeIsRouted = getWorkflowOutgoingEdges(draftEdges, sourceKey).some((edge) => (
      edge.outcomeKey === WORKFLOW_OUTCOME_COMPLETE && edge.toTaskKey != null
    ));

    if (!completeIsRouted) {
      routeOutcomeToExistingTask(fromTask, 'Complete', targetKey);
      return;
    }

    // Bots cannot grow custom outcomes — ignore extra drag connections.
    if (String(fromTask?.handledBy || '').trim().toLowerCase() === MISSION_TASK_HANDLED_BY.BOT) {
      return;
    }

    setConnectToTaskKey(targetKey);
    setAddOutcomeTask(fromTask);
  }, [draftEdges, draftTasks, routeOutcomeToExistingTask]);

  const handleSaveAddOutcome = useCallback((payload = {}) => {
    if (!addOutcomeTask) return;
    const label = payload?.label;
    const existingTargetKey = String(connectToTaskKey || '').trim();
    if (existingTargetKey) {
      routeOutcomeToExistingTask(addOutcomeTask, label, existingTargetKey);
      return;
    }
    continueFromTask(addOutcomeTask, label);
  }, [addOutcomeTask, connectToTaskKey, continueFromTask, routeOutcomeToExistingTask]);

  const handleCloseAddOutcome = useCallback(() => {
    setAddOutcomeTask(null);
    setConnectToTaskKey('');
  }, []);

  const connectToTask = useMemo(() => {
    const taskKey = String(connectToTaskKey || '').trim();
    if (!taskKey) return null;
    return draftTasks.find((task) => task.key === taskKey) || null;
  }, [connectToTaskKey, draftTasks]);

  const renameOutcomeTask = useMemo(() => {
    const taskKey = String(selectedOutcome?.fromTaskKey || '').trim();
    if (!taskKey) return null;
    return draftTasks.find((task) => task.key === taskKey) || null;
  }, [draftTasks, selectedOutcome?.fromTaskKey]);

  const handleSelectedOutcomeChange = useCallback((nextSelection) => {
    setSelectedOutcome(nextSelection || null);
    if (!nextSelection) {
      setRenameOutcomeOpen(false);
      setDeleteOutcomeConfirmOpen(false);
    }
  }, []);

  const handleRenameOutcome = useCallback((payload = {}) => {
    const taskKey = String(selectedOutcome?.fromTaskKey || '').trim();
    const outcomeKey = String(selectedOutcome?.outcomeKey || '').trim();
    const nextLabel = String(payload?.label || '').trim();
    if (!taskKey || !outcomeKey || !nextLabel) return;
    if (outcomeKey === WORKFLOW_OUTCOME_COMPLETE) return;

    const nextTasks = draftTasks.map((task) => {
      if (task.key !== taskKey) return task;
      const outcomes = normalizeWorkflowOutcomes(task?.outcomes, {
        handledBy: task?.handledBy,
      }).map((outcome) => (
        outcome.key === outcomeKey
          ? { ...outcome, label: nextLabel }
          : outcome
      ));
      return {
        ...task,
        outcomes,
      };
    });

    setDraftTasks(nextTasks);
    setSelectedOutcome((prev) => (
      prev && prev.fromTaskKey === taskKey && prev.outcomeKey === outcomeKey
        ? { ...prev, label: nextLabel }
        : prev
    ));
    setRenameOutcomeOpen(false);
  }, [draftTasks, selectedOutcome]);

  const handleConfirmDeleteOutcome = useCallback(() => {
    const taskKey = String(selectedOutcome?.fromTaskKey || '').trim();
    const outcomeKey = String(selectedOutcome?.outcomeKey || '').trim();
    setDeleteOutcomeConfirmOpen(false);
    if (!taskKey || !outcomeKey || outcomeKey === WORKFLOW_OUTCOME_COMPLETE) return;

    const nextTasks = draftTasks.map((task) => {
      if (task.key !== taskKey) return task;
      return {
        ...task,
        outcomes: normalizeWorkflowOutcomes(task?.outcomes, {
          handledBy: task?.handledBy,
        }).filter((outcome) => outcome.key !== outcomeKey),
      };
    });
    const nextEdges = draftEdges.filter((edge) => !(
      edge.fromTaskKey === taskKey && edge.outcomeKey === outcomeKey
    ));

    setDraftTasks(nextTasks);
    setDraftEdges(nextEdges);
    setSelectedOutcome(null);
    setRenameOutcomeOpen(false);
    setFitNonce((value) => value + 1);
  }, [draftEdges, draftTasks, selectedOutcome]);

  const selectedOutcomeLabel = String(selectedOutcome?.label || '').trim() || 'this outcome';

  const handleSaveTask = useCallback(async (payload, options = {}) => {
    const normalized = normalizeMissionTask({
      ...editingTask,
      ...payload,
      key: editingTask?.key || payload?.key,
    });

    const nextTasks = draftTasks.map((task) => (
      task.key === normalized.key ? { ...task, ...normalized } : task
    ));

    let nextEdges;
    if (Array.isArray(options?.edges)) {
      nextEdges = options.edges;
    } else {
      const required = listWorkflowTaskOutcomeKeys(normalized);
      const next = [...draftEdges];
      required.forEach((outcomeKey) => {
        const exists = next.some((edge) => (
          edge.fromTaskKey === normalized.key && edge.outcomeKey === outcomeKey
        ));
        if (!exists) {
          next.push({
            key: `edge_${normalized.key}_${outcomeKey}_end`,
            fromTaskKey: normalized.key,
            outcomeKey,
            toTaskKey: null,
          });
        }
      });
      nextEdges = next.filter((edge) => {
        if (edge.fromTaskKey !== normalized.key) return true;
        return required.includes(edge.outcomeKey);
      });
    }

    pendingCreationRef.current = null;
    setDraftTasks(nextTasks);
    setDraftEdges(nextEdges);
    setTaskEditorOpen(false);
    setEditingTask(null);

    // Persist the whole workflow — task Save feels like it already saved.
    await persistDesign(nextTasks, nextEdges, { close: false, silent: false });
  }, [draftEdges, draftTasks, editingTask, persistDesign]);

  const handleDeleteTask = useCallback(async () => {
    const taskKey = String(editingTask?.key || '').trim();
    if (!taskKey) return;

    // Deleting an unconfirmed + task: restore the pre-create snapshot (no orphan start).
    const pending = pendingCreationRef.current;
    if (pending?.taskKey === taskKey) {
      pendingCreationRef.current = null;
      setDraftTasks(Array.isArray(pending.tasks) ? pending.tasks : []);
      setDraftEdges(Array.isArray(pending.edges) ? pending.edges : []);
      setTaskEditorOpen(false);
      setEditingTask(null);
      setFitNonce((value) => value + 1);
      return;
    }

    const { tasks: nextTasks, edges: nextEdges } = removeWorkflowTaskFromGraph(
      draftTasks,
      draftEdges,
      taskKey,
    );
    pendingCreationRef.current = null;
    setDraftTasks(nextTasks);
    setDraftEdges(nextEdges);
    setTaskEditorOpen(false);
    setEditingTask(null);

    await persistDesign(nextTasks, nextEdges, { close: false, silent: false });
  }, [draftEdges, draftTasks, editingTask, persistDesign]);

  const handleSave = useCallback(async () => {
    await persistDesign(draftTasks, draftEdges, { close: true, silent: false });
  }, [draftEdges, draftTasks, persistDesign]);

  const workflowTitle = String(draftTitle || template?.title || '').trim() || 'this playbook';
  const canDeleteWorkflow = typeof onDelete === 'function' && Boolean(templateKey);

  const handleConfirmDeleteWorkflow = useCallback(async () => {
    if (!canDeleteWorkflow) return;
    setDeleteConfirmOpen(false);
    const didDelete = await onDelete?.(templateKey);
    if (didDelete === false) {
      setSaveError('Could not delete this playbook.');
      return;
    }
    onClose?.();
  }, [canDeleteWorkflow, onClose, onDelete, templateKey]);

  return (
    <>
      <Modal
        iconKey={draftIconKey}
        isOpen={isOpen}
        title="Playbook"
        width={isMobile ? '100%' : '96%'}
        height={isMobile ? '100%' : '92%'}
        onClose={() => onClose?.()}
      >
        <div className="workflow-design-modal-container">
          <div className="workflow-design-modal-toolbar">
            <div className="workflow-design-modal-identity">
              <IconPickerButton
                buttonSize={isMobile ? 40 : 46}
                iconSize={isMobile ? 24 : 28}
                selectedIconKey={draftIconKey}
                title="Choose playbook icon"
                onSelect={handleIconSelect}
              />

              <div className="workflow-design-modal-title">
                <TextInput
                  hybrid
                  placeholder="Playbook title"
                  style={{
                    fontSize: isMobile ? 22 : 28,
                    fontWeight: 700,
                    maxWidth: '100%',
                    paddingBottom: 4,
                    paddingLeft: 10,
                    paddingTop: 4,
                    width: '100%',
                  }}
                  value={draftTitle}
                  onBlur={handleTitleBlur}
                  onChange={(event) => {
                    setDraftTitle(String(event?.target?.value || ''));
                  }}
                  onEnterPress={handleTitleBlur}
                />
              </div>
            </div>

            <div className="workflow-design-modal-toolbar-actions">
              {selectedOutcome ? (
                <>
                  <Button
                    backgroundColor="transparent"
                    foregroundColor={theme.foregroundMuted}
                    iconKey="edit"
                    label="Rename Outcome"
                    onClick={() => setRenameOutcomeOpen(true)}
                  />
                  <Button
                    backgroundColor="transparent"
                    foregroundColor={theme.foregroundMuted}
                    iconKey="delete"
                    label="Delete Outcome"
                    onClick={() => setDeleteOutcomeConfirmOpen(true)}
                  />
                </>
              ) : null}

              <Button
                iconKey="add"
                label="Add Task"
                onClick={handleAddTask}
              />
            </div>
          </div>

          {saveError ? (
            <div className="workflow-design-modal-save-error">
              {saveError}
            </div>
          ) : null}

          <WorkflowDesignerCanvas
            edges={draftEdges}
            fitNonce={fitNonce}
            tasks={draftTasks}
            onAddOutcome={handleOpenAddOutcome}
            onChange={handleGraphChange}
            onConnectTasks={handleConnectTasks}
            onEditTask={handleEditTask}
            onSelectedOutcomeChange={handleSelectedOutcomeChange}
          />
        </div>

        <ModalButtons>
          <ModalButtonSection align="START">
            {canDeleteWorkflow ? (
              <Button
                backgroundColor="transparent"
                enabled={!isSaving}
                foregroundColor={theme.foregroundMuted}
                hoverForegroundColor={theme.foreground}
                iconKey="delete"
                label="Delete this playbook"
                onClick={() => setDeleteConfirmOpen(true)}
              />
            ) : null}
          </ModalButtonSection>

          <ModalButtonSection align="END">
            <Button
              enabled={!isSaving}
              iconKey="close"
              label="Close"
              onClick={() => onClose?.()}
            />
            <Button
              enabled={!isSaving && validation.ok}
              iconKey="check"
              label={isSaving ? 'Saving...' : 'Save'}
              title={validation.ok ? undefined : validation.errors[0]}
              onClick={handleSave}
            />
          </ModalButtonSection>
        </ModalButtons>
      </Modal>

      <Confirm
        confirmLabel="Delete permanently"
        isOpen={deleteConfirmOpen}
        message={`Delete “${workflowTitle}” permanently? This removes the playbook and all of its tasks, checklists, routes, and related data. This cannot be undone.`}
        secondaryLabel="Keep playbook"
        setOpen={setDeleteConfirmOpen}
        title="Delete this playbook?"
        width={480}
        onConfirm={handleConfirmDeleteWorkflow}
        onSecondary={() => setDeleteConfirmOpen(false)}
      />

      <Confirm
        confirmLabel="Delete outcome"
        isOpen={deleteOutcomeConfirmOpen}
        message={`Remove “${selectedOutcomeLabel}”? Operators will no longer see this button. The connected path is removed; other tasks stay in the playbook.`}
        secondaryLabel="Keep outcome"
        setOpen={setDeleteOutcomeConfirmOpen}
        title="Delete this outcome?"
        width={440}
        onConfirm={handleConfirmDeleteOutcome}
        onSecondary={() => setDeleteOutcomeConfirmOpen(false)}
      />

      <MissionTaskModal
        availableFields={availableFields}
        availableTasks={draftTasks}
        baseKey={baseKey}
        edges={draftEdges}
        isOpen={taskEditorOpen}
        mode="edit"
        recordTypeOptions={recordTypeOptions}
        setOpen={handleTaskEditorOpenChange}
        task={editingTask}
        typeLabel={typeLabel}
        onDelete={handleDeleteTask}
        onSave={handleSaveTask}
      />

      <WorkflowAddOutcomeModal
        fromTask={addOutcomeTask}
        isOpen={Boolean(addOutcomeTask)}
        toTask={connectToTask}
        onClose={handleCloseAddOutcome}
        onSave={handleSaveAddOutcome}
      />

      <WorkflowRenameOutcomeModal
        fromTask={renameOutcomeTask}
        initialLabel={selectedOutcome?.label || ''}
        isOpen={renameOutcomeOpen && Boolean(selectedOutcome)}
        onClose={() => setRenameOutcomeOpen(false)}
        onSave={handleRenameOutcome}
      />
    </>
  );
});

WorkflowDesignModal.displayName = 'WorkflowDesignModal';

export default WorkflowDesignModal;
